import { memo, useMemo } from 'react';
import { format, parseISO } from 'date-fns';
import { useTranslation } from '@hooks';
import useMediaQuery from '@mui/material/useMediaQuery';
import TableContainer from '@mui/material/TableContainer';
import Paper from '@mui/material/Paper';
import { Typography } from '@components/Typography';
import Bar from '@components/Charts/Bar';
import { I18N_DATE_FORMAT, getChartDateFormat } from '@helpers/dates';
import { styled } from '@mui/system';

const PaperCss = styled(Paper)({
    width: '100%',
    padding: '24px',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '8px',
    marginBottom: '16px',
    '@media print': {
        breakInside: 'avoid',
    },
});

const ChartWrapper = styled('div')({
    '@media print': {
        height: '280px !important',
    },
});

const chartItemOptions = {
    categoryPercentage: 0.9,
    barPercentage: 1.0,
    borderWidth: 0,
    borderRadius: 8,
};

const createChartData = (labels, data, t) => {
    return {
        labels,
        datasets: [
            {
                label: t('AnalyticsConversion.openedSurveys'),
                data: data.map(d => d.openedSurveys || 0),
                backgroundColor: '#1F4C5C',
                hoverBackgroundColor: '#1F4C5C',
                ...chartItemOptions,
            },
            {
                label: t('AnalyticsConversion.responsesReceived'),
                data: data.map(d => d.responsesReceived || 0),
                backgroundColor: '#BDE0CC',
                hoverBackgroundColor: '#BDE0CC',
                ...chartItemOptions,
            },
            {
                label: t('AnalyticsConversion.proceededToReviewSite'),
                data: data.map(d => d.leaveReviewClicked || 0),
                backgroundColor: '#98CBAD',
                hoverBackgroundColor: '#98CBAD',
                ...chartItemOptions,
            },
            {
                label: t('AnalyticsConversion.reviewsReceived'),
                data: data.map(d => d.reviewsReceived || 0),
                backgroundColor: '#62828D',
                hoverBackgroundColor: '#62828D',
                ...chartItemOptions,
            },
        ],
    };
};

const ConversionTrend = memo(({ data, language, grouping }) => {
    const { t } = useTranslation();
    const isMobile = useMediaQuery('@media (max-width: 1024px)');

    const labels = useMemo(() => {
        return data.map(d =>
            format(parseISO(d.date), getChartDateFormat(grouping), { locale: I18N_DATE_FORMAT[language] }),
        );
    }, [language, data, grouping]);

    const chartOptions = useMemo(
        () => ({
            scales: {
                x: {
                    stacked: false,
                },
                y: {
                    stacked: false,
                },
            },
        }),
        [],
    );

    return (
        <PaperCss elevation={1}>
            <Typography variant="title1" sx={{ marginBottom: '50px' }}>
                {t('AnalyticsConversion.conversionTrend')}
            </Typography>
            <TableContainer>
                <ChartWrapper
                    id="conversion-chart-wrapper"
                    style={{
                        position: 'relative',
                        height: isMobile ? '400px' : '280px',
                        ...(isMobile && { width: '900px' }),
                    }}
                >
                    {labels.length ? (
                        <Bar
                            parentId="conversion-chart-wrapper"
                            data={createChartData(labels, data, t)}
                            labels={labels}
                            legends={t('AnalyticsReviews.totalResponses')}
                            optionsProps={chartOptions}
                        />
                    ) : null}
                </ChartWrapper>
            </TableContainer>
        </PaperCss>
    );
});

ConversionTrend.displayName = 'ConversionTrend';

export default ConversionTrend;
